import { Component, OnInit, inject, NgZone } from '@angular/core';
import { LayoutService } from '@layout/service/app.layout.service';
import { AuthService } from '@core/services/auth/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs';
import { User } from '@core/models';

@Component({
  selector: 'cwa-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private layoutService = inject(LayoutService);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private router = inject(Router);

  constructor() {
    // Only call profile if we have a token but no user data
    if (this.authService.token && !this.authService.user) {
      this.authService
        .profile()
        .pipe(takeUntilDestroyed())
        .subscribe({
          next: () => {
            // Profile loaded successfully, but don't redirect here
            // Let the router guards and ngOnInit handle navigation
          },
          error: () => {
            // On error, redirect to login
            this.router.navigateByUrl('/login');
          },
        });
    }
  }

  private handleInitialRedirect(): void {
    // Only redirect if user is on root path
    const currentUrl = this.router.url;
    if (currentUrl === '/' || currentUrl === '') {
      const user = this.authService.user;

      if (!user) {
        // No user, redirect to login
        this.router.navigateByUrl('/login', { replaceUrl: true });
        return;
      }

      // Check if user has permitted modules and redirect
      if (user.permitted_modules && user.permitted_modules.length > 0) {
        // Prefer brand_dot_com as home page if available
        let targetModule: string;

        if (user.permitted_modules.includes('brand_dot_com')) {
          targetModule = 'brand_dot_com';
        } else {
          // Otherwise use the first permitted module
          targetModule = user.permitted_modules[0];
        }

        const redirectPath = this.getRedirectPathForModule(targetModule);
        this.router.navigateByUrl(redirectPath, { replaceUrl: true });
      } else if (user.is_superuser) {
        // Superuser with no permitted_modules - use default
        this.router.navigateByUrl('/brand/channel-mix', { replaceUrl: true });
      } else {
        // No permissions and not superuser - redirect to profile
        this.router.navigateByUrl('/profile/account', { replaceUrl: true });
      }
    }
  }

  private getRedirectPathForModule(moduleName: string): string {
    const modulePathMapping: { [key: string]: string } = {
      property_report: '/report/property',
      brand_dot_com: '/brand/channel-mix',
      paid_media: '/media/overview',
      social: '/social/facebook',
      reviews: '/reviews/dashboard',
      ai_report: '/ai/ai-report',
      user: '/users',
    };
    return modulePathMapping[moduleName] || '/profile/account';
  }

  ngOnInit(): void {
    // Wait for the router to be ready before checking the URL
    // This ensures we get the actual URL, not just '/'
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1) // Only take the first navigation event
      )
      .subscribe((event: NavigationEnd) => {
        const currentUrl = event.urlAfterRedirects || event.url;

        // Only redirect if we're on the root path
        if (
          (currentUrl === '/' || currentUrl === '') &&
          this.authService.token &&
          this.authService.user
        ) {
          this.handleInitialRedirect();
        }
      });

    //optional configuration with the default configuration
    this.layoutService.config = {
      ripple: false, //toggles ripple on and off
      inputStyle: 'outlined', //default style for input elements
      menuMode: 'static', //layout mode of the menu, valid values are "static", "overlay", "slim", "horizontal", "reveal" and "drawer"
      colorScheme: 'light', //color scheme of the template, valid values are "light", "dim" and "dark"
      theme: 'purple', //default component theme for PrimeNG
      menuTheme: 'colorScheme', //theme of the menu, valid values are "colorScheme", "primaryColor" and "transparent"
      scale: 14, //size of the body font size to scale the whole application
    };

    // Subscribe to route changes to trigger dataLayer push on page navigation
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd) // Filter for NavigationEnd events
      )
      .subscribe(() => {
        // Fetch user details from AuthService (modify according to your app structure)
        const user: User | null = this.authService.user; // Example method to get the user object

        // Initialize and push to window.dataLayer
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'page_view',
          username: (user?.firstName || '') + (user?.lastName || '') || 'anonymous',
          enterprise: user?.enterprises || [],
          // brand: brand,
          hotel: user?.hotels || [],
        });
      });
  }
}
